import { Card, Select, Space, Table, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { initialChoice, needsTarget, targetOptions, type RolloverChoice, type RolloverClassOption, type RolloverRow } from "../app/rollover.ts";

type Props = {
  rows: RolloverRow[];
  targetClasses: RolloverClassOption[];
  choices: Record<string, RolloverChoice>;
  errors: Record<string, string[]>;
  compact: boolean;
  onChange: (studentId: string, choice: RolloverChoice) => void;
};

const outcomes = ["Promote", "Repeat", "Graduate", "Skip"] as const;

/** One source class of the rollover review: a row per student with an editable outcome and target class. */
export function RolloverClassCard({ rows, targetClasses, choices, errors, compact, onChange }: Props) {
  const { t } = useTranslation();
  const first = rows[0];
  if (!first) return null;
  const nextGrade = targetOptions(first, "Promote", targetClasses);
  const classLabel = (c: RolloverClassOption) => `${c.name} · ${c.courseName}`;

  const setClassTarget = (targetClassId: string) => {
    for (const row of rows) {
      const choice = choices[row.studentId];
      if (choice?.outcome === "Promote") onChange(row.studentId, { ...choice, targetClassId });
    }
  };

  const columns = [
    {
      title: t("rollover.columns.student"),
      key: "student",
      render: (_: unknown, row: RolloverRow) => (
        <Link to={`/students/${row.studentId}`}>{[row.lastName, row.firstName, row.middleName].filter(Boolean).join(" ")}</Link>
      ),
    },
    {
      title: t("rollover.columns.outcome"),
      key: "outcome",
      width: 200,
      render: (_: unknown, row: RolloverRow) => {
        const choice = choices[row.studentId] ?? initialChoice(row);
        return (
          <Select<RolloverChoice["outcome"]>
            aria-label={t("rollover.columns.outcome")}
            style={{ width: "100%" }}
            value={choice.outcome}
            disabled={row.note === "AlreadyPlaced"}
            onChange={(outcome) => {
              const options = targetOptions(row, outcome, targetClasses);
              const keep = options.some((c) => c.id === choice.targetClassId) ? choice.targetClassId : undefined;
              onChange(row.studentId, { outcome, targetClassId: keep ?? (options.length === 1 ? options[0]?.id : undefined) });
            }}
            options={outcomes.map((o) => ({ value: o, label: t(`rollover.outcome.${o}`), disabled: o === "Graduate" && !row.canGraduate }))}
          />
        );
      },
    },
    {
      title: t("rollover.columns.targetClass"),
      key: "target",
      width: 230,
      render: (_: unknown, row: RolloverRow) => {
        const choice = choices[row.studentId] ?? initialChoice(row);
        const options = targetOptions(row, choice.outcome, targetClasses);
        const rowErrors = errors[row.studentId] ?? [];
        const invalid = needsTarget(choice) || rowErrors.length > 0;
        return (
          <Space orientation="vertical" size={2} style={{ width: "100%" }}>
            <Select<string | undefined>
              aria-label={t("rollover.columns.targetClass")}
              style={{ width: "100%" }}
              value={choice.targetClassId}
              disabled={options.length === 0}
              status={invalid ? "error" : ""}
              onChange={(targetClassId) => onChange(row.studentId, { ...choice, targetClassId })}
              options={options.map((c) => ({ value: c.id, label: classLabel(c) }))}
            />
            {rowErrors.map((e) => (
              <Typography.Text key={e} type="danger" style={{ fontSize: 12 }}>
                {e}
              </Typography.Text>
            ))}
          </Space>
        );
      },
    },
    {
      title: t("rollover.columns.note"),
      key: "note",
      width: 220,
      render: (_: unknown, row: RolloverRow) => {
        if (row.note === "None") return null;
        const color = row.note === "NoTargetClass" ? "red" : row.note === "ChooseTargetClass" ? "orange" : "default";
        return <Tag color={color}>{t(`rollover.note.${row.note}`, { name: row.placedClassName ?? "" })}</Tag>;
      },
    },
  ];

  return (
    <Card
      size="small"
      title={t("rollover.classHeader", { name: first.sourceClassName, course: first.sourceCourseName, count: rows.length })}
      extra={
        nextGrade.length > 1 ? (
          <Select
            aria-label={t("rollover.classTarget")}
            placeholder={t("rollover.classTarget")}
            style={{ width: compact ? 180 : 240 }}
            onChange={setClassTarget}
            options={nextGrade.map((c) => ({ value: c.id, label: classLabel(c) }))}
          />
        ) : null
      }
    >
      <Table<RolloverRow>
        rowKey="studentId"
        size="small"
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 820 }}
      />
    </Card>
  );
}
